import React, { useState } from 'react';
import type { AreaPreviewResponse } from './api';
import type { DraftBroadcast } from './ComposeForm';

// ─── Public interface ────────────────────────────────────────────────────────

interface PreviewPanelProps {
  draft: DraftBroadcast;
  preview: AreaPreviewResponse;
  onEdit: () => void;
  onSend: () => Promise<void>;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function PreviewPanel(props: PreviewPanelProps): React.ReactElement {
  const { draft, preview, onEdit, onSend } = props;

  const [sending, setSending] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleSend = async () => {
    // First send: show confirm dialog, set confirmed flag.
    if (!confirmed) {
      const shouldSend = window.confirm(
        `Send this push to approximately ${preview.audience_count.toLocaleString()} users?`
      );
      if (!shouldSend) return;
      setConfirmed(true);
    }

    // Either first send confirmed or retry after first send. Execute the send.
    setSending(true);
    try {
      await onSend();
    } finally {
      setSending(false);
    }
  };

  const noUsersInArea = preview.audience_count === 0;

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* Phone-style notification card */}
      <div className="max-w-md mx-auto bg-gray-800 border border-gray-700 rounded-2xl overflow-hidden shadow-lg">
        {/* Notification header */}
        <div className="px-4 py-3 bg-gray-750 border-b border-gray-700">
          <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">
            SocialSpark
          </p>
        </div>

        {/* Notification body */}
        <div className="px-4 py-4 space-y-2">
          {/* Title */}
          <p className="text-sm font-semibold text-white">
            {draft.title}
          </p>

          {/* Body */}
          <p className="text-sm text-gray-300">
            {draft.body}
          </p>

          {/* Deep link (if provided) */}
          {draft.deepLinkUrl && (
            <p className="text-xs text-gray-500 break-all pt-1">
              {draft.deepLinkUrl.length > 60
                ? `${draft.deepLinkUrl.slice(0, 60)}…`
                : draft.deepLinkUrl}
            </p>
          )}
        </div>
      </div>

      {/* Audience summary */}
      <div className="text-center space-y-1">
        <p className="text-sm text-gray-400">
          <span className="font-bold text-white">
            {preview.audience_count.toLocaleString()}
          </span>
          {' users within '}
          <span className="font-semibold text-white">
            {preview.radius_mi.toFixed(1)} mi
          </span>
          {' of ('}
          <span className="font-mono text-gray-300">
            {preview.center_lat.toFixed(4)}, {preview.center_lng.toFixed(4)}
          </span>
          {')'}
        </p>
      </div>

      {/* Action buttons */}
      <div className="flex gap-3 justify-center pt-2">
        {/* Edit button */}
        <button
          type="button"
          onClick={onEdit}
          disabled={sending}
          className="px-4 py-2.5 text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-xl transition-all disabled:opacity-50"
        >
          Edit
        </button>

        {/* Send button */}
        <button
          type="button"
          onClick={handleSend}
          disabled={noUsersInArea || sending}
          className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-violet-600 to-pink-600 rounded-xl hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
        >
          {noUsersInArea
            ? 'No users in this area'
            : `Send to ${preview.audience_count.toLocaleString()} users`}
        </button>
      </div>
    </div>
  );
}

export default PreviewPanel;
